import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getHydraUIAPIQueryOptions, hydraUiApi } from '@/services/api';
import type { ReportingWebhookResult } from '@/services/auth/types';
import { CLIENT_SIDE_RESULT_CAP, COMMAND_DIALOG_QUERY_DEFAULTS } from './constants';
import { filterReportingWebhooks, mapReportingWebhookToSearchResult } from './utils/form-results';
import type { SearchSource } from './utils/source-bookkeeping';

type UseWebhooksSourceOptions = {
  workspaceId: number;
  enabled: boolean;
  query: string;
};

type WebhooksSource = SearchSource<ReportingWebhookResult>;

export function useWebhooksSource({ workspaceId, enabled, query }: UseWebhooksSourceOptions): WebhooksSource {
  const webhooksQuery = useQuery(
    getHydraUIAPIQueryOptions({
      queryKey: ['getReportingWebhooks', { params: { environment_id: workspaceId } }],
      queryFn: ({ signal }) =>
        hydraUiApi.reportingWebhooks.getWebhooks({
          params: { path: { environment_id: String(workspaceId) } },
          signal,
        }),
      queryConfig: {
        enabled,
        ...COMMAND_DIALOG_QUERY_DEFAULTS,
        select: (data) => data.webhooks,
      },
    }),
  );

  const results = useMemo(
    () =>
      filterReportingWebhooks(webhooksQuery.data ?? [], query, CLIENT_SIDE_RESULT_CAP).map((webhook) =>
        mapReportingWebhookToSearchResult(webhook, workspaceId),
      ),
    [webhooksQuery.data, query, workspaceId],
  );

  return { ...webhooksQuery, results };
}
